/**
 * Etiquetas NEW y «Mejorado» de los widgets — modelo único.
 *
 * Qué widget es nuevo o se ha mejorado lo dice el equipo en el backoffice
 * (Widgets → la ficha de cada uno), igual que con los juegos: por widget,
 * «nuevo desde» y «mejorado desde» (un día, AAAA-MM-DD) y, para todos, cuántos
 * días dura cada etiqueta. Se guarda en el Worker (D1 `app_config['widget_marks']`)
 * y llega a la app dentro del feed del Inicio (`widgetMarks`), que ya se pide.
 *
 * Las listas escritas en la app (TC_NEW_BADGES y TC_IMPROVED_BADGES de la galería
 * y sus copias del Editor) son solo la RESERVA de un widget del que el backoffice
 * no ha dicho nada. Con `newSince: null` el widget NO es nuevo aunque la app lo
 * traiga en su lista (y lo mismo con `improvedSince`).
 *
 * Este archivo es la fuente única: lo cargan tal cual la app (script clásico,
 * deja `TCWidgetMarks` en global), el backoffice (backoffice/src/lib/widgetMarks.js)
 * y el Worker (cf-worker/src/lib/widgetMarks.js). Sin DOM en lo que decide; lo que
 * toca la ventana (guardar la última lista y avisar) solo corre en un navegador.
 *
 * Claves: la del widget en la galería (`top-donors`, `level_up`…) o, para un pack
 * de animaciones, `anim-pack-<pack>` (solo NEW: las animaciones van por packs).
 */
(function (root) {
  'use strict';

  const DAY_MS = 86400000;
  const DEFAULT_DAYS = Object.freeze({ newDays: 30, improvedDays: 14 });
  const MAX_ITEMS = 400;
  const FIELDS = ['newSince', 'improvedSince'];
  const KEY_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
  const STORE_KEY = 'tc_widget_marks_v1';
  const EVENT = 'widget-marks:changed';

  const own = (object, key) => !!object && Object.prototype.hasOwnProperty.call(object, key);
  const validDays = (value) => Number.isInteger(value) && value >= 1 && value <= 365;
  const validDay = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && !Number.isNaN(Date.parse(value + 'T00:00:00Z'));

  function cleanDays(raw) {
    return {
      newDays: validDays(raw && raw.newDays) ? raw.newDays : DEFAULT_DAYS.newDays,
      improvedDays: validDays(raw && raw.improvedDays) ? raw.improvedDays : DEFAULT_DAYS.improvedDays
    };
  }

  /**
   * Lo guardado, limpio y en su forma conocida (nunca lanza). De cada widget solo
   * quedan los campos que se dijeron a propósito: un día o `null` («sin etiqueta»).
   * Las claves van ordenadas, para que el mismo contenido dé siempre el mismo texto.
   */
  function sanitize(raw) {
    const out = { days: cleanDays(raw && raw.days), items: {} };
    const items = raw && raw.items && typeof raw.items === 'object' && !Array.isArray(raw.items) ? raw.items : {};
    let count = 0;
    for (const key of Object.keys(items).sort()) {
      if (count >= MAX_ITEMS) break;
      if (!KEY_RE.test(key)) continue;
      const entry = items[key];
      if (!entry || typeof entry !== 'object') continue;
      const clean = {};
      for (const field of FIELDS) {
        if (!own(entry, field)) continue;
        if (entry[field] === null) clean[field] = null;
        else if (validDay(entry[field])) clean[field] = entry[field];
      }
      if (!Object.keys(clean).length) continue;
      out.items[key] = clean;
      count++;
    }
    return out;
  }

  /** ¿No dice nada? (ningún widget marcado y la duración de fábrica). */
  function isEmpty(config) {
    const clean = sanitize(config);
    return !Object.keys(clean.items).length
      && clean.days.newDays === DEFAULT_DAYS.newDays && clean.days.improvedDays === DEFAULT_DAYS.improvedDays;
  }

  /** ¿Sigue en vigor una etiqueta que empezó el día `since` y dura `days` días? */
  function markLive(since, days, now) {
    const start = validDay(since) ? new Date(since + 'T00:00:00').getTime() : NaN;
    const at = now == null ? Date.now() : now;
    return Number.isFinite(start) && at >= start && at < start + days * DAY_MS;
  }

  /** ¿Sigue en vigor una etiqueta de las escritas en la app, que caduca el día `until`? */
  function untilLive(until, now) {
    const at = now == null ? Date.now() : now;
    return validDay(until) && at <= new Date(until + 'T23:59:59').getTime();
  }

  /** Último día (AAAA-MM-DD) en que se ve una etiqueta que empezó `since` y dura `days` días. */
  function lastDay(since, days) {
    return validDay(since) ? new Date(Date.parse(since + 'T00:00:00Z') + (days - 1) * DAY_MS).toISOString().slice(0, 10) : '';
  }

  // Lo que dice el backoffice manda; si de ese widget no ha dicho nada, la fecha de la app.
  function decide(config, key, field, fallbackUntil, now) {
    const entry = config && config.items ? config.items[key] : null;
    if (own(entry, field)) return markLive(entry[field], field === 'newSince' ? config.days.newDays : config.days.improvedDays, now);
    return untilLive(fallbackUntil, now);
  }
  const isNewIn = (config, key, fallbackUntil, now) => decide(config, key, 'newSince', fallbackUntil, now);
  const isImprovedIn = (config, key, fallbackUntil, now) => decide(config, key, 'improvedSince', fallbackUntil, now);

  // ── En la app: la última lista recibida ────────────────────────
  // Se guarda para que al arrancar las etiquetas salgan ya como quedaron, sin
  // esperar al feed (y sin repintar la galería cuando llega igual).
  const inBrowser = typeof root.document !== 'undefined';
  let current = sanitize(null);
  let currentText = JSON.stringify(current);
  if (inBrowser) {
    try {
      const stored = root.localStorage.getItem(STORE_KEY);
      if (stored) {
        current = sanitize(JSON.parse(stored));
        currentText = JSON.stringify(current);
      }
    } catch (_) { /* sin almacenamiento: se queda la de fábrica */ }
  }

  /** Pone la lista que llega del Worker. Devuelve true (y avisa) solo si cambia algo. */
  function set(raw) {
    const next = sanitize(raw);
    const text = JSON.stringify(next);
    if (text === currentText) return false;
    current = next;
    currentText = text;
    if (inBrowser) {
      try { root.localStorage.setItem(STORE_KEY, text); } catch (_) { /* sin almacenamiento */ }
      try { root.dispatchEvent(new root.CustomEvent(EVENT)); } catch (_) { /* sin eventos */ }
    }
    return true;
  }

  root.TCWidgetMarks = {
    DEFAULT_DAYS, MAX_ITEMS, FIELDS, EVENT, STORE_KEY,
    sanitize, isEmpty, cleanDays, validDay, validDays, markLive, untilLive, lastDay, isNewIn, isImprovedIn,
    set,
    get: () => current,
    /** Claves de las que el backoffice ha dicho algo. */
    keys: () => Object.keys(current.items),
    /** ¿Lleva NEW? `fallbackUntil` = la fecha de la lista de la app para esa clave (o nada). */
    isNew: (key, fallbackUntil) => isNewIn(current, key, fallbackUntil),
    /** ¿Lleva «Mejorado»? (quien pinta decide que NEW manda si lleva las dos). */
    isImproved: (key, fallbackUntil) => isImprovedIn(current, key, fallbackUntil)
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
